import { type FormEvent, useEffect, useState } from "react";
import { AvatarPickerDialog } from "@/components/avatar-picker-dialog";
import { OwnerAvatar } from "@/components/owner-avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/auth-context";
import {
  useIdleSpotReaper,
  useNebiusRegions,
  useNightlyReaper,
  useSetIdleSpotReaper,
  useSetNebiusRegions,
  useSetNightlyReaper,
  useSetSpotPoolAccess,
  useSpotPoolAccess,
} from "@/hooks/use-admin-settings";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  useApiKeys,
  useCreateApiKey,
  useCreateUser,
  useRevealApiKey,
  useRevokeApiKey,
  useUpdateSshKey,
  useUsers,
} from "@/hooks/use-users";
import { ACCENT_PRESETS, applyAccentHue, readAccentHue } from "@/lib/accent";
import type { User } from "@/lib/types";
import { cn } from "@/lib/utils";

function AddUserDialog() {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sshPubKey, setSshPubKey] = useState("");
  const [error, setError] = useState("");
  const createUser = useCreateUser();

  const reset = () => {
    setUsername("");
    setPassword("");
    setName("");
    setEmail("");
    setSshPubKey("");
    setError("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    createUser.mutate(
      { username, password, name, email, ssh_pub_key: sshPubKey },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Failed to create user");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) reset();
      }}
    >
      <DialogTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            className="border-border bg-card text-foreground/80 hover:bg-accent hover:text-foreground"
          />
        }
      >
        + Add User
      </DialogTrigger>
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Add User</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Create a new fleet user account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Username</Label>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                placeholder="jdoe"
                className="border-border bg-card text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                placeholder="********"
                className="border-border bg-card text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Display Name</Label>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              placeholder="Jane Doe"
              className="border-border bg-card text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              placeholder="jdoe@inferize.ai"
              className="border-border bg-card text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">SSH Public Key</Label>
            <Input
              value={sshPubKey}
              onChange={(event) => setSshPubKey(event.target.value)}
              required
              placeholder="ssh-ed25519 AAAA..."
              className="border-border bg-card font-mono text-xs text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-border text-foreground/70 hover:bg-accent"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createUser.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {createUser.isPending ? "Creating..." : "Add User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserSshKeyForm({ user }: { user: User }) {
  const [sshKey, setSshKey] = useState(user.ssh_pub_key ?? "");
  const [error, setError] = useState("");
  const updateSshKey = useUpdateSshKey();

  const isDirty = sshKey !== (user.ssh_pub_key ?? "");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    updateSshKey.mutate(
      { userId: user.id, sshPubKey: sshKey },
      {
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Failed to update SSH key");
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">SSH Public Key</Label>
      <div className="flex gap-2">
        <Input
          value={sshKey}
          onChange={(event) => setSshKey(event.target.value)}
          placeholder="ssh-ed25519 AAAA..."
          className="flex-1 border-border bg-card font-mono text-xs text-foreground placeholder:text-muted-foreground"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!isDirty || !sshKey.trim() || updateSshKey.isPending}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {updateSshKey.isPending ? "..." : "Update"}
        </Button>
      </div>
      {error && <div className="text-xs text-rose-600">{error}</div>}
    </form>
  );
}

const REVEAL_AUTO_HIDE_MS = 30_000;

function UserApiKeys({ user }: { user: User }) {
  const { data: keys = [] } = useApiKeys(user.id);
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();
  const revealKey = useRevealApiKey();
  const [keyName, setKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createError, setCreateError] = useState("");
  const [revealed, setRevealed] = useState<{ keyId: number; key: string } | null>(null);
  const [revealErrors, setRevealErrors] = useState<Record<number, string>>({});
  const [pendingRevealKeyId, setPendingRevealKeyId] = useState<number | null>(null);

  // Auto-hide revealed plaintext after a short window — reduces shoulder-surfing
  // risk if the user walks away with the panel open. Matches AWS / GCP secret
  // consoles, which never leave a one-time secret on screen indefinitely.
  useEffect(() => {
    if (!revealed) return;
    const timeout = setTimeout(() => setRevealed(null), REVEAL_AUTO_HIDE_MS);
    return () => clearTimeout(timeout);
  }, [revealed]);

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!keyName.trim()) return;
    setCreateError("");
    createKey.mutate(
      { userId: user.id, name: keyName },
      {
        onSuccess: (data) => {
          setCreatedKey(data.key);
          setKeyName("");
        },
        onError: (error) => {
          setCreateError(error instanceof Error ? error.message : "Failed to create key");
        },
      },
    );
  };

  const handleReveal = (keyId: number) => {
    setRevealErrors((errors) => {
      if (!(keyId in errors)) return errors;
      const { [keyId]: _removed, ...rest } = errors;
      return rest;
    });
    setPendingRevealKeyId(keyId);
    revealKey.mutate(
      { userId: user.id, keyId },
      {
        // Guard against stale callbacks: useRevealApiKey is a single shared
        // mutation, so if the user clicks Reveal on A then B before A resolves,
        // A's callbacks still fire. Only apply results / clear the spinner
        // when this callback is for the currently-pending key.
        onSuccess: (data) => {
          setPendingRevealKeyId((current) => {
            if (current !== keyId) return current;
            setRevealed({ keyId, key: data.key });
            return null;
          });
        },
        onError: (error) => {
          setPendingRevealKeyId((current) => {
            if (current !== keyId) return current;
            const message = error instanceof Error ? error.message : "Failed to reveal key";
            setRevealErrors((errors) => ({ ...errors, [keyId]: message }));
            return null;
          });
        },
      },
    );
  };

  const handleCopy = (value: string) => {
    void navigator.clipboard.writeText(value);
  };

  const activeKeys = keys.filter((key) => !key.revoked_at);

  return (
    <div className="mt-3 space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">API Keys</Label>

      {createdKey && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
          <div className="mb-1 text-xs text-emerald-700">
            New key created (copy now, shown once):
          </div>
          <code className="break-all font-mono text-xs text-emerald-800">{createdKey}</code>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setCreatedKey(null)}
            className="mt-1 text-emerald-700 hover:text-emerald-800"
          >
            Dismiss
          </Button>
        </div>
      )}

      {activeKeys.length > 0 && (
        <div className="space-y-1">
          {activeKeys.map((key) => {
            const isRevealed = revealed?.keyId === key.id;
            const errorForKey = revealErrors[key.id] ?? null;
            const isRevealing = pendingRevealKeyId === key.id;
            return (
              <div key={key.id} className="rounded-lg bg-accent px-3 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs text-foreground/80">{key.name}</span>
                    {isRevealed ? (
                      <code className="ml-2 break-all font-mono text-[10px] text-emerald-700">
                        {revealed.key}
                      </code>
                    ) : (
                      <span className="ml-2 font-mono text-[10px] text-muted-foreground">
                        {key.prefix}...
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {isRevealed ? (
                      <>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => handleCopy(revealed.key)}
                          className="text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                        >
                          Copy
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setRevealed(null)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Hide
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => handleReveal(key.id)}
                        disabled={isRevealing}
                        className="text-foreground/70 hover:bg-accent-foreground/10"
                      >
                        {isRevealing ? "..." : "Reveal"}
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => revokeKey.mutate({ userId: user.id, keyId: key.id })}
                      className="text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
                {errorForKey && <div className="mt-1 text-[10px] text-rose-600">{errorForKey}</div>}
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={handleCreate} className="flex gap-2">
        <Input
          value={keyName}
          onChange={(event) => setKeyName(event.target.value)}
          placeholder="Key name"
          className="flex-1 border-border bg-card text-xs text-foreground placeholder:text-muted-foreground"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!keyName.trim() || createKey.isPending}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Create
        </Button>
      </form>
      {createError && <div className="text-[10px] text-rose-600">{createError}</div>}
    </div>
  );
}

function UserCard({ user }: { user: User }) {
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { user: currentUser } = useAuth();
  const canEditAvatar =
    !user.is_service && (currentUser?.id === user.id || currentUser?.is_admin === true);

  const avatar = (
    <OwnerAvatar
      name={user.name}
      avatarUrl={user.avatar_url}
      isService={user.is_service}
      size="lg"
    />
  );

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <div className="flex w-full items-center gap-2.5 px-4 py-3 transition-colors hover:bg-accent">
        {canEditAvatar ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            title="Change avatar"
            className="rounded-full transition-all hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {avatar}
          </button>
        ) : (
          avatar
        )}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex flex-1 items-center justify-between text-left"
        >
          <div>
            <div className="text-[13px] font-medium text-foreground">{user.name}</div>
            <div className="text-[11px] text-muted-foreground">{user.email}</div>
          </div>
          <span
            className={cn(
              "text-[11px] font-medium",
              user.has_ssh_key ? "text-emerald-600" : "text-amber-600",
            )}
          >
            {user.has_ssh_key ? "SSH key \u2713" : "No SSH key"}
          </span>
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border/50 bg-accent/50 px-4 pb-4 pt-2">
          <UserSshKeyForm user={user} />
          <UserApiKeys user={user} />
        </div>
      )}

      {canEditAvatar && (
        <AvatarPickerDialog user={user} open={pickerOpen} onOpenChange={setPickerOpen} />
      )}
    </div>
  );
}

function MyAccountSection({ currentUser }: { currentUser: User }) {
  return (
    <div>
      <h2 className="mb-3 text-[15px] font-semibold text-foreground">My Account</h2>
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <UserCard user={currentUser} />
      </div>
    </div>
  );
}

function AdminUsersSection() {
  const { data: users = [], isLoading } = useUsers();

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-foreground">Users</h2>
        <AddUserDialog />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <span className="text-sm text-muted-foreground">Loading users...</span>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          {users
            .filter((user) => !user.is_service)
            .map((user) => (
              <UserCard key={user.id} user={user} />
            ))}
          {users.filter((user) => !user.is_service).length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No users</div>
          )}
        </div>
      )}

      {/* Service accounts */}
      {users.filter((user) => user.is_service).length > 0 && (
        <>
          <h3 className="mb-3 mt-6 text-[13px] font-medium text-muted-foreground">
            Service Accounts
          </h3>
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            {users
              .filter((user) => user.is_service)
              .map((user) => (
                <UserCard key={user.id} user={user} />
              ))}
          </div>
        </>
      )}
    </div>
  );
}

function AppearanceSection() {
  const [hue, setHue] = useState(readAccentHue);
  const pickHue = (next: number) => {
    applyAccentHue(next);
    setHue(next);
  };

  return (
    <div className="mb-6">
      <h2 className="mb-3 text-[15px] font-semibold text-foreground">Appearance</h2>
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <Label className="text-xs font-medium text-muted-foreground">Accent color</Label>
        <div className="mt-3 flex flex-wrap gap-3">
          {ACCENT_PRESETS.map((preset) => {
            const selected = preset.hue === hue;
            return (
              <button
                key={preset.name}
                type="button"
                onClick={() => pickHue(preset.hue)}
                title={preset.name}
                aria-label={preset.name}
                aria-pressed={selected}
                className={cn(
                  "size-8 rounded-full ring-offset-2 ring-offset-card transition-all hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected && "ring-2 ring-ring",
                )}
                style={{ backgroundColor: `oklch(0.6 0.15 ${preset.hue})` }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SpotPoolAccessSection() {
  const { data: state } = useSpotPoolAccess();
  const setAccess = useSetSpotPoolAccess();

  // Mirrors the backend default (SPOT_POOL_OPEN_TO_ALL_DEFAULT): while the
  // query is in flight the switch shows the default, disabled. Right unless an
  // admin previously locked the pool, in which case it reads ON for ~100ms —
  // never actionable. IdleSpotReaperSection's `?? false` matches *its* default.
  const openToAll = state?.open_to_all ?? true;

  return (
    <div>
      <h2 className="mb-3 text-[15px] font-semibold text-foreground">Access</h2>
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-foreground">
              Pools &mdash; everyone can modify
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              Let any user add, edit, enable, start-now, and delete spot pool targets. Turn this off
              to restrict those actions to admins &mdash; everyone can still view the pool and its
              machines either way.
            </div>
          </div>
          <Switch
            checked={openToAll}
            disabled={!state || setAccess.isPending}
            onCheckedChange={(checked) => setAccess.mutate(!!checked)}
          />
        </div>
      </div>
    </div>
  );
}

function NebiusRegionsSection() {
  const { data: state } = useNebiusRegions();
  const setRegions = useSetNebiusRegions();
  const regions = state?.regions ?? [];
  const enabledRegions = regions.filter((region) => region.enabled).map((region) => region.region);

  function toggle(regionName: string, enabled: boolean) {
    setRegions.mutate(
      enabled
        ? [...enabledRegions, regionName]
        : enabledRegions.filter((name) => name !== regionName),
    );
  }

  return (
    <div>
      <h2 className="mb-3 text-[15px] font-semibold text-foreground">Nebius regions</h2>
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="px-4 py-3 text-[11px] text-muted-foreground">
          Pools and provision jobs launch new machines only in enabled regions. A region Nebius
          starts offering shows up here switched off. Switching a region off stops new machines
          there; machines already in it keep running, and reservations are not affected.
        </div>
        {state?.discovery_error ? (
          <div className="border-t border-border px-4 py-3 text-[11px] text-destructive">
            Region discovery failed ({state.discovery_error}); only enabled regions are listed.
          </div>
        ) : null}
        {regions.map((region) => (
          <div
            key={region.region}
            className="flex items-center justify-between gap-4 border-t border-border px-4 py-3"
          >
            <div className="min-w-0">
              <div className="font-mono text-[13px] font-medium text-foreground">
                {region.region}
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {region.gpu_types.length > 0
                  ? region.gpu_types.map((gpuType) => gpuType.toUpperCase()).join(", ")
                  : "No supported GPU offered right now"}
              </div>
            </div>
            <Switch
              checked={region.enabled}
              disabled={!state || setRegions.isPending}
              onCheckedChange={(checked) => toggle(region.region, !!checked)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** "24 hours", "36 hours", "14 days" — hours under two days, whole days above. */
function formatStoppedThreshold(hours: number): string {
  if (hours < 48 || hours % 24 !== 0) return `${hours} hours`;
  return `${hours / 24} days`;
}

function NightlyReaperSection() {
  const { data: state } = useNightlyReaper();
  const setReaper = useSetNightlyReaper();

  // Both fall back to the un-armed state while the query is in flight, so a
  // slow load never renders the reaper as armed.
  const enabled = state?.enabled ?? false;
  const dryRun = state?.dry_run ?? true;
  const stoppedFor = formatStoppedThreshold(state?.reap_after_stopped_hours ?? 24);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-foreground">Nightly Reaper</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Permanently delete machines stopped for {stoppedFor}+, and disks Fleet created that are
            attached to nothing. Runs nightly at 04:00 Israel time and posts a summary to the alert
            channel. A machine stopped on Friday and not restarted is deleted over the weekend
            &mdash; protect anything that must survive from the machine menu.
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={!state || setReaper.isPending}
          onCheckedChange={(checked) => setReaper.mutate({ enabled: !!checked })}
        />
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-foreground">Dry run</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {dryRun
              ? "Reports what it would delete without deleting anything. Read a few nightly summaries, then turn this off to arm it."
              : "ARMED — deletions are real and cannot be undone."}
          </div>
        </div>
        <Switch
          checked={dryRun}
          // Only meaningful once the reaper is on; leaving it interactive while
          // disabled invites arming a job that isn't running and then being
          // surprised the night it is switched on.
          disabled={!state || !enabled || setReaper.isPending}
          onCheckedChange={(checked) => setReaper.mutate({ dry_run: !!checked })}
        />
      </div>
    </div>
  );
}

function IdleSpotReaperSection() {
  const { data: state } = useIdleSpotReaper();
  const setReaper = useSetIdleSpotReaper();

  const enabled = state?.enabled ?? false;
  const thresholdMinutes = state?.threshold_minutes ?? 20;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-foreground">Idle Spot Reaper</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Automatically stop spot instances across all providers that no user has acquired in the
            last {thresholdMinutes} minutes. Also shuts down pool machines once their pool is
            outside its active window &mdash; leave this off and pool schedules never stop anything.
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={!state || setReaper.isPending}
          onCheckedChange={(checked) => setReaper.mutate(!!checked)}
        />
      </div>
    </div>
  );
}

function AutomationSection() {
  return (
    <div className="space-y-3">
      <h2 className="text-[15px] font-semibold text-foreground">Automation</h2>
      <IdleSpotReaperSection />
      <NightlyReaperSection />
    </div>
  );
}

export function SettingsPage() {
  usePageTitle("Settings");
  const { user: currentUser } = useAuth();

  if (!currentUser) return null;

  const isAdmin = currentUser.is_admin;

  return (
    <div className="space-y-6 px-4 py-4 sm:px-6 sm:py-5">
      <AppearanceSection />
      {isAdmin ? (
        <>
          <AdminUsersSection />
          <SpotPoolAccessSection />
          <NebiusRegionsSection />
          <AutomationSection />
        </>
      ) : (
        <MyAccountSection currentUser={currentUser} />
      )}
    </div>
  );
}
